import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GearSix,
  GitBranch,
  SidebarSimple,
  Sparkle
} from '@phosphor-icons/react';

/**
 * BuilderLayout - Split-panel layout for the Unified Agent Builder
 * 
 * Left Panel: Chat or Visual Flow Editor (tabbed) - 70%
 * Right Panel: Live Preview + Test Console + Quick Actions - 30% (collapsible)
 */
const BuilderLayout = ({ 
  mode, 
  onModeChange, 
  leftPanel, 
  rightPanel,
  quickActionsPanel
}) => {
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  
  const modes = [
    { id: 'visual', label: 'Visual Flow', icon: GitBranch },
    { id: 'config', label: 'Config', icon: GearSix }
  ];

  return (
    <div className="flex-1 flex overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Left Panel - Builder */}
      <div 
        className={`flex flex-col border-r border-white/10 transition-all duration-300 ${
          rightPanelCollapsed ? 'w-full' : 'w-[70%]'
        }`}
      >
        {/* Mode Tabs */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#12131a] border-b border-white/10">
          <div className="flex items-center gap-1">
            {modes.map(m => (
              <button
                key={m.id}
                onClick={() => onModeChange(m.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${mode === m.id 
                    ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' 
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }
                `}
                data-testid={`mode-tab-${m.id}`}
              >
                <m.icon size={16} />
                {m.label}
                {m.id === 'visual' && mode !== 'visual' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                    New
                  </span>
                )}
              </button>
            ))}
          </div>
          
          {/* Panel Toggle Button */}
          <button
            onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
              text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            title={rightPanelCollapsed ? 'Show Preview Panel' : 'Hide Preview Panel'}
            data-testid="toggle-preview-panel"
          >
            {rightPanelCollapsed ? (
              <>
                <SidebarSimple size={14} />
                Show Preview
              </>
            ) : (
              <>
                <SidebarSimple size={14} />
                Hide Preview
              </>
            )}
          </button>
        </div>
        
        {/* Left Panel Content */}
        <div className="flex-1 overflow-hidden">
          {leftPanel}
        </div>
      </div>
      
      {/* Right Panel - Preview + Test + Quick Actions (Collapsible) */}
      <AnimatePresence>
        {!rightPanelCollapsed && (
          <motion.div 
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '30%', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col bg-[#0d0e14] overflow-hidden"
          >
            <div className="flex-1 overflow-hidden">
              {rightPanel}
            </div>
            {/* Quick Actions at bottom of right panel */}
            {quickActionsPanel}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Collapsed Preview Indicator */}
      {rightPanelCollapsed && (
        <div 
          className="w-10 bg-[#0d0e14] border-l border-white/10 flex flex-col items-center py-4 cursor-pointer hover:bg-white/5 transition-colors"
          onClick={() => setRightPanelCollapsed(false)}
          title="Expand Preview Panel"
        >
          <SidebarSimple size={16} className="text-slate-500 mb-2" />
          <span className="text-[10px] text-slate-500 writing-mode-vertical transform rotate-180" 
            style={{ writingMode: 'vertical-rl' }}>
            Preview
          </span>
        </div>
      )}
    </div>
  );
};

export default BuilderLayout;
